import { useEffect, useRef, type ReactElement, type ReactNode } from 'react'

import { Popover } from '@base-ui/react/popover'
import { AnimatePresence, motion, type Variants } from 'motion/react'

import { useReducedMotion } from '../../../hooks/use-reduced-motion'
import { cn } from '../../../utils/cn'
import type { ButtonProps } from '../../atoms/button/button'

export interface OmniTablePopoverProps {
  trigger: ReactElement<React.ComponentPropsWithRef<'button'> | ButtonProps>
  /** Existing filter or editor content; the table does not own its data or actions. */
  children: ReactNode
  open: boolean
  onOpenChange: (open: boolean) => void
  label: string
  align?: Popover.Positioner.Props['align']
  className?: string
  animated?: boolean
}

const panelVariants: Variants = {
  closed: {
    opacity: 0,
    scale: 0.96,
    y: -6,
    transition: {
      duration: 0.12,
      ease: 'easeIn',
      staggerChildren: 0.015,
      staggerDirection: -1
    }
  },
  open: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.2,
      ease: [0.16, 1, 0.3, 1],
      staggerChildren: 0.025
    }
  }
}
const instantVariants: Variants = {
  closed: { opacity: 0, transition: { duration: 0 } },
  open: { opacity: 1, transition: { duration: 0 } }
}

/** Shared popup motion and focus lifecycle, independent of the panel's content. */
export function OmniTablePopover({
  trigger,
  children,
  open,
  onOpenChange,
  label,
  align = 'start',
  className,
  animated = true
}: OmniTablePopoverProps) {
  const reducedMotion = useReducedMotion()
  const shouldAnimate = animated && !reducedMotion
  const actionsRef = useRef<Popover.Root.Actions>(null)
  const popupRef = useRef<HTMLDivElement>(null)

  // AnimatePresence retains the last open element during exit; disable it immediately.
  useEffect(() => {
    popupRef.current?.toggleAttribute('inert', !open)
    if (open) popupRef.current?.removeAttribute('aria-hidden')
    else popupRef.current?.setAttribute('aria-hidden', 'true')
  }, [open])

  return (
    <Popover.Root
      open={open}
      onOpenChange={onOpenChange}
      actionsRef={actionsRef}
    >
      <Popover.Trigger render={trigger} />
      <AnimatePresence
        onExitComplete={() => {
          if (!open) actionsRef.current?.unmount()
        }}
      >
        {open && (
          <Popover.Portal keepMounted>
            <Popover.Positioner sideOffset={8} align={align} className='z-50'>
              <Popover.Popup
                ref={popupRef}
                aria-label={label}
                className={cn(
                  'origin-(--transform-origin) outline-none',
                  className
                )}
                render={
                  <motion.div
                    variants={shouldAnimate ? panelVariants : instantVariants}
                    initial={shouldAnimate ? 'closed' : false}
                    animate='open'
                    exit='closed'
                  />
                }
              >
                {children}
              </Popover.Popup>
            </Popover.Positioner>
          </Popover.Portal>
        )}
      </AnimatePresence>
    </Popover.Root>
  )
}
